/**
 * Last Failed command - Retrieve cached test failure data
 */

import type { Config } from '@/config';
import type { Command } from '@/types';
import { CacheIdDetector } from '@/core/build-detector';
import { CacheApiClient } from '@/services/cache-api';
import { CiCollector } from '@/collectors/ci';
import { EnvironmentUtils } from '@/utils/env';
import { logger, setVerbose } from '@/utils/logger';
import { z } from 'zod';

/**
 * CLI options specific to the last failed command
 */
export interface LastFailedOptions {
  cacheId?: string; // Custom cache ID override
  branch?: string; // Custom branch name override
  commit?: string; // Custom commit hash override
  shard?: string; // Shard specification (e.g., "1/3")
  token?: string;
  verbose?: boolean;
}

/**
 * Parsed shard information
 */
interface ShardInfo {
  shardIndex: number;
  shardTotal: number;
}

/**
 * Zod schema for last failed options validation
 */
export const LastFailedOptionsSchema = z.object({
  cacheId: z.string().optional(),
  branch: z.string().optional(),
  commit: z.string().optional(),
  shard: z
    .string()
    .optional()
    .refine(
      val => {
        if (!val) return true; // Optional field
        const match = val.match(/^(\d+)\/(\d+)$/);
        if (!match) return false;
        const [, index, total] = match;
        const shardIndex = parseInt(index || '0', 10);
        const shardTotal = parseInt(total || '0', 10);
        return shardIndex > 0 && shardTotal > 0 && shardIndex <= shardTotal;
      },
      {
        message:
          'Shard must be in format "index/total" where index > 0 and index <= total (e.g., "1/3")',
      }
    ),
  token: z.string().optional(),
  verbose: z.boolean().optional().default(false),
});

export type ValidatedLastFailedOptions = z.infer<
  typeof LastFailedOptionsSchema
>;

/**
 * Cached failure data from API
 */
interface CacheFailureData {
  cacheId: string;
  failures: Array<{
    file: string;
    testTitle: string;
  }>;
  branch: string;
  repository: string;
}

/**
 * Last Failed command implementation
 */
export class LastFailedCommand implements Command {
  readonly name = 'last-failed';
  readonly description = 'Get last failed test cases for Playwright execution';

  constructor(private readonly config: Config) {}

  /**
   * Execute the last failed command
   */
  async execute(options: ValidatedLastFailedOptions): Promise<void> {
    const isDebugMode =
      options.verbose ||
      EnvironmentUtils.getStringEnv('TESTDINO_RUNTIME') === 'development';

    // Set verbose mode for logger
    setVerbose(isDebugMode);

    try {
      if (isDebugMode) {
        logger.debug('Retrieving last failed tests...');
        if (
          options.cacheId ||
          options.branch ||
          options.commit ||
          options.shard
        ) {
          logger.debug(
            `Custom overrides: cacheId=${options.cacheId || 'auto-detect'}, branch=${options.branch || 'auto-detect'}, commit=${options.commit || 'auto-detect'}, shard=${options.shard || 'none'}`
          );
        }
      }

      // Step 1: Parse shard information if provided
      const shardInfo = options.shard
        ? this.parseShardInfo(options.shard)
        : null;

      if (isDebugMode && shardInfo) {
        logger.debug(`Shard: ${shardInfo.shardIndex}/${shardInfo.shardTotal}`);
      }

      // Step 2: Determine cache ID with custom overrides
      const cacheId = await this.determineCacheId(
        options.cacheId,
        options.branch,
        options.commit
      );

      if (isDebugMode && cacheId) {
        logger.debug(`Cache ID: ${cacheId}`);
      }

      // Step 3: Get cached failure data
      const failureData = await this.getCachedFailures(
        cacheId,
        options,
        shardInfo,
        isDebugMode
      );

      if (!failureData?.failures || failureData.failures.length === 0) {
        if (isDebugMode) {
          if (shardInfo) {
            logger.info(
              `No failed test cases found for shard ${shardInfo.shardIndex}/${shardInfo.shardTotal}`
            );
          } else {
            logger.info('No last failed test cases found');
          }
        }
        process.exit(0);
        return;
      }

      if (isDebugMode) {
        logger.debug(`Found: ${failureData.failures.length} failed tests`);
        logger.debug(`Branch: ${failureData.branch}`);
        logger.debug(`Repository: ${failureData.repository}`);
      }

      // Step 4: Format output for Playwright
      const playwrightArgs = this.formatPlaywrightArgs(failureData.failures);

      // Step 5: Output result (to stdout for shell substitution)
      console.log(playwrightArgs);
    } catch (_error) {
      if (isDebugMode) {
        const errorMessage =
          _error instanceof Error ? _error.message : 'Unknown error';
        logger.error(`Failed to retrieve cached failures: ${errorMessage}`);
      }
      process.exit(1);
    }
  }

  /**
   * Parse shard information from string format (e.g., "1/3")
   */
  private parseShardInfo(shard: string): ShardInfo | null {
    const match = shard.match(/^(\d+)\/(\d+)$/);
    if (!match) return null;

    const [, index, total] = match;
    return {
      shardIndex: parseInt(index || '0', 10),
      shardTotal: parseInt(total || '0', 10),
    };
  }

  /**
   * Determine cache ID using same logic as cache store command
   */
  private async determineCacheId(
    customCacheId?: string,
    customBranch?: string,
    customCommit?: string
  ): Promise<string | null> {
    try {
      const cacheIdInfo = await CacheIdDetector.detectCacheId(
        customCacheId,
        customBranch,
        customCommit
      );
      return cacheIdInfo.cacheId;
    } catch (_error) {
      return null;
    }
  }

  /**
   * Get cached failure data from API
   */
  private async getCachedFailures(
    cacheId: string | null,
    options: ValidatedLastFailedOptions,
    shardInfo: ShardInfo | null,
    isDebugMode: boolean
  ): Promise<CacheFailureData | null> {
    const apiClient = new CacheApiClient(this.config);

    try {
      // Get cache for specific cache ID
      if (cacheId) {
        // Determine query parameters based on whether cache ID is custom
        const queryParams: {
          branch?: string;
          commit?: string;
          shard?: number;
          pipelineId?: string;
        } = {};

        // If custom cache ID is provided, pass branch/commit as query params
        if (options.cacheId) {
          if (options.branch) queryParams.branch = options.branch;
          if (options.commit) queryParams.commit = options.commit;
        } else {
          // If auto-generated cache ID:
          // - Branch affects cache ID generation (not passed as query param)
          // - Commit is passed as query param only
          if (options.commit) queryParams.commit = options.commit;
        }

        // Add shard index if specified
        if (shardInfo) {
          queryParams.shard = shardInfo.shardIndex;
        }

        // Detect and add pipelineId from CI environment
        // This ensures we get cache data for the current pipeline run,
        // not a different pipeline that may have the same cacheId
        const ciMetadata = CiCollector.collect();
        if (ciMetadata.pipeline.id && ciMetadata.pipeline.id !== 'unknown') {
          queryParams.pipelineId = ciMetadata.pipeline.id;
        }

        if (isDebugMode) {
          logger.debug(`Fetching cache for: ${cacheId}`);
          if (Object.keys(queryParams).length > 0) {
            logger.debug(`Query parameters: ${JSON.stringify(queryParams)}`);
          }
        }

        const cache = await apiClient.getCacheData(cacheId, queryParams);
        if (cache) {
          return cache;
        }

        if (isDebugMode) {
          if (shardInfo) {
            logger.debug(
              `No cache found for ${cacheId} (shard ${shardInfo.shardIndex}/${shardInfo.shardTotal})`
            );
          } else {
            logger.debug(`No cache found for ${cacheId}`);
          }
        }
      }

      return null;
    } catch (error) {
      if (isDebugMode) {
        logger.debug(
          `API error: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
      return null;
    }
  }

  /**
   * Format failure data for Playwright command
   */
  private formatPlaywrightArgs(
    failures: Array<{ file: string; testTitle: string }>
  ): string {
    if (failures.length === 0) {
      return '';
    }

    // Group failures by file
    const failuresByFile = failures.reduce<Record<string, string[]>>(
      (acc, failure) => {
        if (!acc[failure.file]) {
          acc[failure.file] = [];
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        acc[failure.file]!.push(failure.testTitle);
        return acc;
      },
      {}
    );

    const files = Object.keys(failuresByFile);

    // If all failures are from a single file
    if (files.length === 1) {
      const file = files[0];
      if (file) {
        const testTitles = failuresByFile[file];
        if (testTitles) {
          const grepPattern = testTitles
            .map((title: string) => this.escapeForGrep(title))
            .join('|');
          return `${file} -g "${grepPattern}"`;
        }
      }
    }

    // Multiple files - use global grep pattern
    const allTestTitles = failures.map(f => this.escapeForGrep(f.testTitle));
    const grepPattern = allTestTitles.join('|');
    return `-g "${grepPattern}"`;
  }

  /**
   * Escape test title for Playwright's -g flag
   */
  private escapeForGrep(testTitle: string): string {
    // Escape special regex characters for Playwright's grep
    return testTitle
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // Escape regex special chars
      .replace(/"/g, '\\"') // Escape double quotes for shell
      .trim();
  }
}
